import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { getScreenTier, SCREEN_TIERS } from "../utils/breakpoints";

const LayoutContext = createContext(null);
const ADDRESSBOOK_PANEL_GROUPS = [
  ["details", "map", "comments", "chat", "images"],
  ["journal", "reminders", "files"],
];
const MYMAGIC_PANEL_GROUPS = [
  ["voiceProfiles", "textProfiles", "enrichmentRules"],
];
const DEFAULT_OPEN_PANELS = {
  contacts: true,
  campaignList: true,
  campaignDetail: true,
  campaignContacts: true,
  campaignAnalytics: false,
  loanInbox: true,
  loanEmail: true,
  loanFields: true,
  loanFiles: true,
  loanEmails: false,
  loanLoans: false,
  dealList: true,
  dealDetail: true,
  details: true,
  properties: true,
  propertyDetail: false,
  map: false,
  journal: false,
  reminders: false,
  files: false,
  images: false,
  comments: false,
  chat: false,
  voiceProfiles: true,
  textProfiles: false,
  voiceProfileDetail: false,
  enrichmentRules: false,
  enrichmentRulesDetail: false,
};
const DEFAULT_ADDRESSBOOK_TOOLBAR = {
  searchQuery: "",
  categoryFilter: "",
  categoryOptions: [],
  onSearchChange: null,
  onSearchSubmit: null,
  onCategoryFilterChange: null,
};
const DEFAULT_PROPERTIES_TOOLBAR = {
  searchQuery: "",
  onSearchChange: null,
  onSearchSubmit: null,
};
const DEFAULT_MATCH_TOOLBAR = {
  propertySearchQuery: "",
  onPropertySearchChange: null,
  onPropertySearchSubmit: null,
  contactSearchQuery: "",
  onContactSearchChange: null,
  onContactSearchSubmit: null,
};

const normalizeOpenPanels = (panels, changedKey = null) => {
  const next = { ...panels };
  [...ADDRESSBOOK_PANEL_GROUPS, ...MYMAGIC_PANEL_GROUPS].forEach((group) => {
    const openKeys = group.filter((key) => next[key]);
    if (openKeys.length <= 1) return;

    const preferred = changedKey && group.includes(changedKey) && next[changedKey]
      ? changedKey
      : openKeys[openKeys.length - 1];

    group.forEach((key) => {
      next[key] = key === preferred;
    });
  });
  return next;
};

export const useLayout = () => {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error("useLayout must be used within LayoutProvider");
  return ctx;
};

export const LayoutProvider = ({ children }) => {
  const [screenTier, setScreenTier] = useState(() => getScreenTier(window.innerWidth));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Reminder inbox is an independent overlay layer, NOT a member of the
  // mutually-exclusive panel groups: it must open over any page without
  // disturbing whatever workspace panels are already open.
  const [reminderInboxOpen, setReminderInboxOpen] = useState(false);
  // App-wide text size (small / medium / large), selected in the footer and
  // applied as a proportional zoom so every font scales together without the
  // reflow quirks of browser zoom.
  const [uiSize, setUiSize] = useState(() => {
    try {
      const saved = localStorage.getItem("cs_ui_size");
      if (saved === "small" || saved === "medium" || saved === "large") return saved;
    } catch {}
    return "small";
  });
  useEffect(() => {
    const zoom = { small: 1, medium: 1.15, large: 1.3 }[uiSize] || 1;
    document.body.style.zoom = zoom;
    try { localStorage.setItem("cs_ui_size", uiSize); } catch {}
  }, [uiSize]);
  const [addressbookToolbar, setAddressbookToolbarState] = useState(DEFAULT_ADDRESSBOOK_TOOLBAR);
  const [propertiesToolbar, setPropertiesToolbarState] = useState(DEFAULT_PROPERTIES_TOOLBAR);
  const [matchToolbar, setMatchToolbarState] = useState(DEFAULT_MATCH_TOOLBAR);
  const [openPanels, setOpenPanels] = useState(() => {
    try {
      const saved = localStorage.getItem("cs_open_panels");
      if (saved) return normalizeOpenPanels({ ...DEFAULT_OPEN_PANELS, ...JSON.parse(saved) });
    } catch {}
    return DEFAULT_OPEN_PANELS;
  });

  useEffect(() => {
    try { localStorage.setItem("cs_open_panels", JSON.stringify(openPanels)); } catch {}
  }, [openPanels]);

  useEffect(() => {
    let raf;
    const handleResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const tier = getScreenTier(window.innerWidth);
        setScreenTier(tier);
        if (tier === SCREEN_TIERS.SMALL) setSidebarOpen(false);
      });
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);
  const toggleReminderInbox = useCallback(() => setReminderInboxOpen((prev) => !prev), []);
  const closeReminderInbox = useCallback(() => setReminderInboxOpen(false), []);
  const togglePanel = useCallback(
    (key) => setOpenPanels((prev) => normalizeOpenPanels({ ...prev, [key]: !prev[key] }, key)),
    []
  );
  const setPanels = useCallback(
    (updates) =>
      setOpenPanels((prev) => {
        const changedKey = Object.keys(updates).find((k) => updates[k] === true) || null;
        return normalizeOpenPanels({ ...prev, ...updates }, changedKey);
      }),
    []
  );
  const isPanelOpen = useCallback((key) => !!openPanels[key], [openPanels]);
  const setAddressbookToolbar = useCallback(
    (updates) => setAddressbookToolbarState((prev) => ({ ...prev, ...updates })),
    []
  );
  const resetAddressbookToolbar = useCallback(
    () => setAddressbookToolbarState(DEFAULT_ADDRESSBOOK_TOOLBAR),
    []
  );
  const setPropertiesToolbar = useCallback(
    (updates) => setPropertiesToolbarState((prev) => ({ ...prev, ...updates })),
    []
  );
  const resetPropertiesToolbar = useCallback(
    () => setPropertiesToolbarState(DEFAULT_PROPERTIES_TOOLBAR),
    []
  );
  const setMatchToolbar = useCallback(
    (updates) => setMatchToolbarState((prev) => ({ ...prev, ...updates })),
    []
  );
  const resetMatchToolbar = useCallback(
    () => setMatchToolbarState(DEFAULT_MATCH_TOOLBAR),
    []
  );

  const value = useMemo(() => ({
    screenTier,
    isSmall: screenTier === SCREEN_TIERS.SMALL,
    isMedium: screenTier === SCREEN_TIERS.MEDIUM,
    isLarge: screenTier === SCREEN_TIERS.LARGE,
    sidebarOpen,
    setSidebarOpen,
    toggleSidebar,
    reminderInboxOpen,
    toggleReminderInbox,
    closeReminderInbox,
    uiSize,
    setUiSize,
    openPanels,
    togglePanel,
    setPanels,
    isPanelOpen,
    addressbookToolbar,
    setAddressbookToolbar,
    resetAddressbookToolbar,
    propertiesToolbar,
    setPropertiesToolbar,
    resetPropertiesToolbar,
    matchToolbar,
    setMatchToolbar,
    resetMatchToolbar,
  }), [screenTier, sidebarOpen, toggleSidebar, reminderInboxOpen, toggleReminderInbox, closeReminderInbox, uiSize, openPanels, togglePanel, setPanels, isPanelOpen, addressbookToolbar, setAddressbookToolbar, resetAddressbookToolbar, propertiesToolbar, setPropertiesToolbar, resetPropertiesToolbar, matchToolbar, setMatchToolbar, resetMatchToolbar]);

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};
